/*
 * The Foozie cart drawer: the RULES, and nothing else (session A4, 2026-09-24).
 *
 * Pure functions, no DOM, so bin/test-fz-cart.mjs can run every rule offline.
 * The server (snippets/fz-cart-body.liquid) renders the authoritative drawer;
 * these rules exist so the drawer can move BEFORE the server answers a tap:
 * the bar, the headline, the bundle slot and the totals update the moment a
 * stepper is pressed, and the server's render then lands on top. When the two
 * agree nothing visibly changes. bin/fz-cart-journey.mjs asserts that they
 * agree, market by market, after every step, so a Liquid rule and a JS rule
 * cannot drift apart unnoticed.
 *
 * Why the tiers are what they are (plan section 3.4, W-03):
 * - THE 4TH PAIR IS COUNTED IN PAIRS. MonsterCart counted money only, so at 4
 *   pairs in the EU it still said "Add EUR14". The store's one automatic
 *   discount, "Bundle - FREE 4th Pair", is buy 3 get 1 at 100% off with no
 *   per-order cap (Admin API, 2026-09-24), so 8 pairs give 2 free.
 * - FREE SHIPPING IS COUNTED IN MONEY, after discounts, in the market's own
 *   currency, against market_content.free_shipping_threshold. Lines that do
 *   not ship (Covered Shipping, gift cards) do not count toward it.
 * - A "pair" is a line the bundle discount COVERS, decided by the server
 *   (snippets/fz-cart-body.liquid): the tag `bundle-4th-free`, or until that
 *   tag exists, the discount's own eight handles. Not "any slipper sock": the
 *   next new design is a slipper sock the discount may not cover yet, and the drawer
 *   never promises a free pair it cannot deliver.
 */

export const PAIRS_PER_FREE = 4;

/**
 * @typedef {object} Line
 * @property {string} key
 * @property {number} variantId
 * @property {number} qty
 * @property {number} unitCents - the variant's price in the cart currency, before any discount
 * @property {number} finalCents - the line's price after discounts (the server's word)
 * @property {number} compareCents - compare-at per unit, or unitCents when there is none
 * @property {boolean} pair - a slipper-sock line
 * @property {boolean} ships - requires shipping
 * @property {boolean} [covered] - Covered Shipping: ships nothing, yet checkout counts it toward free shipping (OI-124)
 * @property {number} [freeQty] - units an automatic discount made free (server only)
 * @property {boolean} [bundle] - a bundle, which the 2-bundle offer counts (server only)
 * @property {number} [offerCents] - what the 2-bundle offer took off this line (server only)
 */

/**
 * Totals as the SERVER computed them: what the drawer shows once Shopify answers.
 * @param {Line[]} lines
 * @param {number} [cartDiscountCents] - cart-level codes, which no line carries
 */
export function serverTotals(lines, cartDiscountCents = 0) {
  let pairs = 0;
  let freePairs = 0;
  let totalCents = 0;
  let uncountedCents = 0;
  let compareTotalCents = 0;
  let pairsCents = 0;
  for (const l of lines) {
    totalCents += l.finalCents;
    compareTotalCents += l.compareCents * l.qty;
    // What checkout counts toward free shipping: everything that ships, and
    // Covered Shipping (OI-124). A gift card is unproven and stays out.
    if (!l.ships && !l.covered) uncountedCents += l.finalCents;
    if (l.pair) {
      pairs += l.qty;
      pairsCents += l.finalCents;
      // Units an AUTOMATIC discount made free on this line, counted by the
      // server from the line's allocations. Shopify usually splits the free
      // pair onto a line of its own at 0, but counting allocations rather
      // than zero-priced lines holds whether it splits or not.
      freePairs += l.freeQty || 0;
    }
  }
  // A cart-level code comes off the goods; shipping never goes on them. The
  // server's total is the same figure (fz-cart-body.liquid, never
  // cart.total_price, which carries shipping once an address is set).
  totalCents = Math.max(0, totalCents - cartDiscountCents);
  return {
    pairs,
    freePairs,
    totalCents,
    eligibleCents: Math.max(0, totalCents - uncountedCents),
    savingsCents: Math.max(0, compareTotalCents - totalCents),
    pairsCents,
  };
}

/**
 * Totals as they WILL be, predicted before the server answers.
 * Every pair costs its unit price, and the bundle makes the cheapest
 * floor(pairs / 4) of them free, which is how Shopify applies a buy-X-get-Y
 * discount. Discounts other than the bundle are carried over as they stood.
 * @param {Line[]} lines
 * @param {number} [otherDiscountCents] - discounts the server applied that are not the bundle
 */
export function predictTotals(lines, otherDiscountCents = 0) {
  const units = [];
  let grossCents = 0;
  let uncountedCents = 0;
  let compareTotalCents = 0;
  let pairs = 0;
  let pairsGrossCents = 0;
  for (const l of lines) {
    if (l.qty <= 0) continue;
    const gross = l.unitCents * l.qty;
    grossCents += gross;
    compareTotalCents += l.compareCents * l.qty;
    if (!l.ships && !l.covered) uncountedCents += gross;
    if (l.pair) {
      pairs += l.qty;
      pairsGrossCents += gross;
      for (let i = 0; i < l.qty; i++) units.push(l.unitCents);
    }
  }
  const freePairs = Math.floor(pairs / PAIRS_PER_FREE);
  units.sort((a, b) => a - b);
  let bundleCents = 0;
  for (let i = 0; i < freePairs; i++) bundleCents += units[i];
  const totalCents = Math.max(0, grossCents - bundleCents - otherDiscountCents);
  return {
    pairs,
    freePairs,
    totalCents,
    eligibleCents: Math.max(0, totalCents - uncountedCents),
    savingsCents: Math.max(0, compareTotalCents - totalCents),
    pairsCents: Math.max(0, pairsGrossCents - bundleCents),
  };
}

/**
 * The discount the server applied beyond the bundle (a code, a future
 * automatic), so a prediction does not silently drop it.
 * @param {Line[]} lines
 * @param {number} serverTotalCents
 */
export function otherDiscountCents(lines, serverTotalCents) {
  const predicted = predictTotals(lines, 0).totalCents;
  return Math.max(0, predicted - serverTotalCents);
}

/**
 * The 2-bundle offer (Luke, 2026-10-29): DiscountOS's "Any 2 bundles for $59",
 * one automatic product discount per market. The server rendered the nudge
 * (exactly one bundle) and the applied row (its own title and amount); this
 * predicts both for a stepper on a bundle line before Shopify answers.
 *
 * THE DISCOUNT IS THE SERVER'S WORD wherever it can be. With the same number
 * of bundles the server priced, its amount stands. Below two there is none.
 * At exactly two it is the two bundles' price less the offer's, which is what
 * "any 2 for $59" means. Past two the app's rule is the store's, not ours
 * (no live cart has measured three), so the server's last amount is carried
 * until it answers rather than a guess.
 *
 * @param {Line[]} lines
 * @param {{ priceCents: number, units: number, discountCents: number } | null | undefined} server
 *   the server's `bundleOffer`; priceCents 0 means the market sells no bundles
 */
export function bundleOfferState(lines, server) {
  if (!server || !server.priceCents) return { on: false, units: 0, nudge: false, discountCents: 0 };
  let units = 0;
  let grossCents = 0;
  for (const l of lines) {
    if (!l.bundle || l.qty <= 0) continue;
    units += l.qty;
    grossCents += l.unitCents * l.qty;
  }
  let discountCents;
  if (units === server.units) discountCents = server.discountCents;
  else if (units < 2) discountCents = 0;
  else if (units === 2) discountCents = Math.max(0, grossCents - server.priceCents);
  else discountCents = server.discountCents;
  return { on: true, units, nudge: units === 1, discountCents };
}

/**
 * Apply a quantity change the shopper just made. Setting the free line's
 * quantity is never offered (it has no stepper), so a change targets one key.
 * @param {Line[]} lines
 * @param {string} key
 * @param {number} qty
 */
export function withQuantity(lines, key, qty) {
  return lines.map((l) => (l.key === key ? { ...l, qty: Math.max(0, qty) } : l));
}

/**
 * Add one unit of a variant, as the one-tap add and the upsell do.
 * @param {Line[]} lines
 * @param {Omit<Line, 'key' | 'qty' | 'finalCents'>} variant
 * @param {number} [qty]
 */
export function withAdded(lines, variant, qty = 1) {
  const existing = lines.find((l) => l.variantId === variant.variantId && l.finalCents !== 0);
  if (existing) return withQuantity(lines, existing.key, existing.qty + qty);
  return [...lines, { ...variant, key: `pending-${variant.variantId}`, qty, finalCents: variant.unitCents * qty }];
}

/**
 * The drawer's state: two tiers, one headline, and what the bundle slot says.
 *
 * @param {ReturnType<typeof serverTotals>} t
 * @param {object} ctx
 * @param {number} ctx.thresholdCents - market_content.free_shipping_threshold in cents; 0 turns the shipping tier off
 * @param {string} ctx.market - localization.market.handle
 * @param {number | null} [ctx.nextPairCents] - what the one-tap add would cost, for the UK line;
 *   null when no pair line can take one more (its stock is all in the cart)
 * @param {number | null} [ctx.leadPairCents] - the newest pair's price, which orders the tiers;
 *   defaults to nextPairCents
 */
export function tierState(t, { thresholdCents, market, nextPairCents = null, leadPairCents = nextPairCents }) {
  const shippingOn = thresholdCents > 0;
  const shippingUnlocked = shippingOn && t.eligibleCents >= thresholdCents;
  const remainingCents = shippingOn ? Math.max(0, thresholdCents - t.eligibleCents) : 0;
  const bundleUnlocked = t.freePairs > 0;
  const intoBundle = t.pairs % PAIRS_PER_FREE;

  const shippingProgress = shippingOn ? Math.min(1, t.eligibleCents / thresholdCents) : 0;
  const bundleProgress = bundleUnlocked ? 1 : intoBundle / PAIRS_PER_FREE;
  const pairsNeeded = bundleUnlocked ? 0 : PAIRS_PER_FREE - intoBundle;

  // ONE BAR, two tiers (Luke, Decisions, 2026-09-26: "only one progress bar,
  // tiers for the free shipping and free 4th pair, rather than 2 separate
  // bars"). Each tier owns an equal stage of one track and fills it by its
  // OWN measure: money for shipping (the goods, as checkout counts them),
  // pairs for the bundle. No shared axis exists: the pair tier is a count,
  // and at three pairs the fourth adds a pair and no money.
  // ORDER: the tier this cart reaches first by the price of the newest pair.
  // Shipping leads when the threshold is under three pairs' price, which is
  // what the free fourth costs (AU, NZ, CA at full price); the pair leads on
  // UK, US and EU clearance. It depends on prices, not progress, so the
  // markers do not swap as a cart fills. One exception: when exactly ONE
  // tier is unlocked it goes first, so the fill can reach its marker.
  const tiers = [];
  if (t.pairs > 0) tiers.push({ id: "bundle", done: bundleUnlocked, progress: bundleProgress });
  if (shippingOn) tiers.push({ id: "shipping", done: shippingUnlocked, progress: shippingProgress });
  if (tiers.length === 2) {
    const shippingLeads = leadPairCents != null && thresholdCents < (PAIRS_PER_FREE - 1) * leadPairCents;
    if (shippingLeads) tiers.reverse();
    if (tiers[1].done && !tiers[0].done) tiers.reverse();
  }
  const stage = tiers.length ? 1 / tiers.length : 0;
  const nextIndex = tiers.findIndex((x) => !x.done);
  const fill =
    nextIndex === -1
      ? tiers.length
        ? 1
        : 0
      : Math.round((nextIndex * stage + tiers[nextIndex].progress * stage) * 10000) / 10000;
  const next = nextIndex === -1 ? null : tiers[nextIndex].id;

  // The one line names the NEXT unlock.
  let headline;
  if (next === "shipping") headline = "shipping_remaining";
  else if (next === "bundle") headline = "pair_remaining";
  else if (!tiers.length) headline = "none";
  else if (tiers.length === 2) headline = "all_unlocked";
  else headline = tiers[0].id === "shipping" ? "shipping_unlocked" : "bundle_unlocked";

  // The slot under the lines. MonsterCart showed "Bundle & Save!" whatever the
  // cart held; the plan's fixes are the nudge at 3 pairs, the UK line at 2,
  // and hiding the promise once it has been kept.
  let slot = "banner";
  if (t.pairs > 0 && intoBundle === PAIRS_PER_FREE - 1 && nextPairCents != null) {
    // Only with something to add: a variant whose stock is all in the cart
    // cannot take the one-tap add, and an empty slot says nothing.
    slot = "one_more";
  } else if (
    market === "gb" &&
    t.pairs === 2 &&
    !bundleUnlocked &&
    shippingOn &&
    nextPairCents != null &&
    t.eligibleCents + nextPairCents >= thresholdCents
  ) {
    // "Add a third pair: the fourth is free, and so is shipping." Said only
    // when it is TRUE: on UK clearance three pairs are GBP30, under GBP45.
    slot = "uk_third";
  } else if (bundleUnlocked) {
    slot = "unlocked";
  }

  return {
    shipping: {
      on: shippingOn,
      unlocked: shippingUnlocked,
      remainingCents,
      progress: shippingProgress,
    },
    bundle: {
      unlocked: bundleUnlocked,
      progress: bundleProgress,
      free: t.freePairs,
      toNext: PAIRS_PER_FREE - intoBundle,
    },
    bar: {
      order: tiers.map((x) => x.id),
      markers: tiers.map((x, i) => ({ tier: x.id, at: Math.round((i + 1) * stage * 10000) / 10000, done: x.done })),
      fill,
      next,
      pairsNeeded,
    },
    headline,
    slot,
    pairs: t.pairs,
    perPairCents: bundleUnlocked && t.pairs > 0 ? Math.round(t.pairsCents / t.pairs) : null,
    pairsCents: t.pairsCents,
  };
}

/**
 * A money formatter derived from the SERVER's own rendering of 1,234,567.89 in
 * the cart currency, so the client can never disagree with Liquid's `money`
 * about the symbol, the separators or where the symbol goes. MonsterCart's
 * "Covered Shipping For EUR2" on the UK and US stores is exactly what a
 * formatter that assumed its own currency produces.
 *
 * @param {string} probe - `{{ 123456789 | money }}`, e.g. "£1,234,567.89" or "1.234.567,89 €"
 * @returns {(cents: number, opts?: { trim?: boolean }) => string}
 */
export function moneyFormatter(probe) {
  // (?:567) after the backreference: a bare \1567 is read as an octal escape.
  const m = /1(\D?)234\1(?:567)(\D)89/.exec(probe) || /1(\D?)234\1(?:568)()/.exec(probe);
  if (!m) {
    return (cents) => (cents / 100).toFixed(2);
  }
  const thousands = m[1] || "";
  const decimal = m[2] || ".";
  const before = probe.slice(0, m.index);
  const after = probe.slice(m.index + m[0].length);
  const hasDecimals = Boolean(m[2]);
  // `exact` keeps the cents a no-decimals format would round away, for the two
  // amounts that are a division (per pair, and a quarter of the total). Same
  // rule as snippets/fz-cart-money-exact.liquid, including its limit: above 999
  // the store's own rounding stands.
  const exactSep = thousands === "." ? "," : ".";
  return (cents, { trim = false, exact = false } = {}) => {
    const negative = cents < 0;
    const abs = Math.abs(Math.round(cents));
    const frac = String(abs % 100).padStart(2, "0");
    const keepCents = !hasDecimals && exact && frac !== "00" && abs < 100000;
    // A format with no decimals ROUNDS, as Shopify's amount_no_decimals does:
    // the probe's 1,234,567.89 comes back as 1,234,568. Flooring here printed
    // "$7" where the page printed "$8" for $7.50 (thefoozie.co, 2026-09-25).
    const units = hasDecimals || keepCents ? Math.floor(abs / 100) : Math.round(abs / 100);
    const whole = String(units).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    if (keepCents) return `${negative ? "-" : ""}${before}${whole}${exactSep}${frac}${after}`;
    const showFrac = hasDecimals && !(trim && frac === "00");
    return `${negative ? "-" : ""}${before}${whole}${showFrac ? decimal + frac : ""}${after}`;
  };
}

/**
 * Fill a template the server rendered with [name] holes: "Add [amount] to get
 * FREE shipping!". Square brackets, not braces: in Shopify's Liquid a `}`
 * inside {{ ... }} ENDS the output tag, so `t: amount: '{amount}'` cut the tag
 * in half, and Shopify's GitHub sync silently dropped every file that did it.
 * @param {string} template
 * @param {Record<string, string | number>} values
 */
export function fill(template, values) {
  return template.replace(/\[(\w+)\]/g, (all, k) => (k in values ? String(values[k]) : all));
}
